import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { API_BASE } from "../lib/api";
import { Card, CardHeader, CardTitle, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Loader2, ScanEye, Plus, ArrowRight } from "lucide-react";

const API = `${API_BASE}/api`;

// One-line status pill copy — same map used by CompetitorReportsPage.
// Duplicated intentionally: this file lives under components/ and importing
// from a page-level constant would tangle the dependency tree.
const STATUS_STYLE = {
  draft:            { color: "bg-zinc-100 text-zinc-700 border-zinc-200",       label: "Draft" },
  discovering:      { color: "bg-blue-50 text-blue-700 border-blue-200",         label: "Finding…" },
  pending_confirm:  { color: "bg-amber-50 text-amber-700 border-amber-200",      label: "Confirm" },
  generating:       { color: "bg-indigo-50 text-indigo-700 border-indigo-200",   label: "Generating…" },
  ready:            { color: "bg-emerald-50 text-emerald-700 border-emerald-200",label: "Ready" },
  failed:           { color: "bg-red-50 text-red-700 border-red-200",             label: "Failed" },
};

/**
 * Compact list of competitor reports attached to one client. Rendered on
 * ProjectDetailPage so reps see every competitor deck they've built for the
 * account without having to filter the global list.
 */
export default function ClientCompetitorReports({ clientId, clientName }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!clientId) return;
    axios.get(`${API}/clients/${clientId}/competitor-reports`)
      .then((r) => setRows(r.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [clientId]);

  if (loading) return null;
  if (rows.length === 0) return null;  // hide when empty — the intake form
                                       // itself is where reps pick a client
  return (
    <Card className="border" data-testid="client-competitor-reports">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center justify-between gap-2">
          <span className="flex items-center gap-2">
            <ScanEye className="h-5 w-5 text-indigo-600" /> Competitor Reports
            <Badge variant="secondary">{rows.length}</Badge>
          </span>
          <Button asChild variant="outline" size="sm" data-testid="new-report-for-client">
            <Link to={`/prospect-iq/competitor-reports/new`}>
              <Plus className="h-3.5 w-3.5 mr-1" /> New report
            </Link>
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {rows.slice(0, 6).map((r) => {
          const s = STATUS_STYLE[r.status] || STATUS_STYLE.draft;
          const when = r.created_at ? new Date(r.created_at).toLocaleDateString() : "—";
          return (
            <Link
              key={r.id}
              to={`/prospect-iq/competitor-reports/${r.id}`}
              className="flex items-center justify-between gap-2 border rounded-lg px-3 py-2 hover:bg-zinc-50 transition"
              data-testid={`client-report-${r.id}`}
            >
              <div className="min-w-0">
                <div className="font-medium text-sm truncate">{r.business_name}</div>
                <div className="text-xs text-muted-foreground">
                  {when}{r.created_by_name ? ` · ${r.created_by_name}` : ""}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Badge variant="outline" className={s.color}>{s.label}</Badge>
                <ArrowRight className="h-4 w-4 text-muted-foreground" />
              </div>
            </Link>
          );
        })}
        {rows.length > 6 && (
          <Link to="/prospect-iq/competitor-reports"
                className="text-xs text-indigo-600 hover:underline block text-center pt-1">
            View all {rows.length} reports →
          </Link>
        )}
      </CardContent>
    </Card>
  );
}
